import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";

const API_BASE = 'https://backend.aktcorp.com/compliances';

const EditEvent = () => {
  const [events, setEvents] = useState([]);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [showPage, setShowPage] = useState(false);

  const token = localStorage.getItem("authToken");

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPage(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(API_BASE, { headers });
      setEvents(response.data.data || []); // assuming API response format
    } catch (error) {
      console.error("Error fetching compliances:", error);
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onloadend = () => {
      const base64Data = reader.result.split(",")[1];

      if (type === "image") {
        setEditData((prev) => ({ ...prev, imageBase64: base64Data }));
        setPreviewImage(reader.result);
      } else if (type === "pdf") {
        setEditData((prev) => ({ ...prev, pdfBase64: base64Data }));
        setPreviewPdf(reader.result);
      }
    };
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      imageBase64: editData.imageBase64 || undefined,
      pdfBase64: editData.pdfBase64 || undefined,
    };

    try {
      await axios.put(`${API_BASE}/${editData.id}`, payload, { headers });
      setLoading(false);
      setEditData(null);
      fetchEvents();
    } catch (error) {
      setLoading(false);
      console.error("Compliance update failed:", error);
    }
  };

  if (!showPage) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="w-16 h-16 border-4 border-t-transparent border-blue-600 rounded-full"
        ></motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white shadow-md rounded-lg">
      <motion.h2
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-3xl font-bold text-gray-900 text-center mb-4 bg-gradient-to-r from-blue-500 to-purple-500 text-transparent bg-clip-text"
      >
        Edit Image & PDF
      </motion.h2>

      {events.map((item) => (
        <div key={item.id} className="border-b py-3 flex justify-between items-center">
          <img src={item.imageUrl} alt="Event" className="w-16 h-16 object-cover rounded-md" />
          <button onClick={() => setEditData(item)} className="text-blue-500">
            ✏️ Edit
          </button>
        </div>
      ))}

      {editData && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4 mt-20 z-50">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white p-6 rounded-lg shadow-lg w-11/12 max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <motion.h2
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-2xl font-bold text-blue-800 text-center mb-4"
            >
              Edit Compliance
            </motion.h2>

            {/* Current Image */}
            <div className="mb-4">
              <p className="font-medium">Current Image:</p>
              <img
                src={previewImage || editData.imageUrl}
                alt="Preview"
                className="w-full h-40 object-cover rounded-lg border"
              />
            </div>

            {/* Upload New Image */}
            <input
              type="file"
              accept="image/*"
              className="w-full mb-4 border border-gray-300 rounded-md p-2"
              onChange={(e) => handleFileChange(e, "image")}
            />

            {/* Current PDF */}
            <div className="mb-4">
              <p className="font-medium">Current PDF:</p>
              <iframe
                src={previewPdf || editData.pdfUrl}
                className="w-full h-40 border rounded-lg"
                title="Current PDF"
              ></iframe>
            </div>

            {/* Upload New PDF */}
            <input
              type="file"
              accept="application/pdf"
              className="w-full mb-4 border border-gray-300 rounded-md p-2"
              onChange={(e) => handleFileChange(e, "pdf")}
            />

            <div className="flex justify-between mt-4">
              <button
                onClick={() => setEditData(null)}
                className="bg-gray-500 text-white px-4 py-2 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="bg-blue-500 text-white px-4 py-2 rounded-md"
                disabled={loading}
              >
                {loading ? "Updating..." : "Update"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default EditEvent;
